/**
 * 牌的数据模型
 *
 * 立直麻将共 136 张牌 = 34 种 × 4 张
 *
 * 34 种索引（本项目统一使用）：
 *   0-8   1m-9m   （万子 manzu）
 *   9-17  1p-9p   （筒子 pinzu）
 *   18-26 1s-9s   （索子 souzu）
 *   27-33 1z-7z   （字牌 honor: 东南西北白发中）
 */

/** 花色 */
export type Suit = 'm' | 'p' | 's' | 'z';

/** 数牌数字 1-9；字牌 1-7（东=1 南=2 西=3 北=4 白=5 发=6 中=7） */
export type TileNum = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

/** 字牌数字 1-7 */
export type HonorNum = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/** 34 种牌的索引 0-33 */
export type TileIndex = number;

/** 牌实例（136 张之一） */
export interface Tile {
  /** 花色 */
  readonly suit: Suit;
  /** 数字 */
  readonly num: TileNum | HonorNum;
  /** 唯一 id 0-135（用于区分同种牌的不同实例，如赤宝牌） */
  readonly id: number;
  /** 是否赤宝牌（5m/5p/5s 的红牌） */
  readonly red: boolean;
}

// ── 常量 ──────────────────────────────────────────────────────────

/** 每种牌的数量 */
export const COPIES_PER_TILE = 4;

/** 牌种数 */
export const NUM_TILE_TYPES = 34;

/** 牌总数 */
export const NUM_TILES = 136;

/** 全 34 种牌的字符串表示（索引 → 名称） */
export const TILE_NAMES: readonly string[] = [
  ...'123456789'.split('').map((n) => `${n}m`),
  ...'123456789'.split('').map((n) => `${n}p`),
  ...'123456789'.split('').map((n) => `${n}s`),
  ...'1234567'.split('').map((n) => `${n}z`),
];

/** 花色 → 索引偏移 */
const SUIT_OFFSET: Record<Suit, number> = { m: 0, p: 9, s: 18, z: 27 };

/** 花色字符 → Suit */
const SUIT_FROM_CHAR: Record<string, Suit> = { m: 'm', p: 'p', s: 's', z: 'z' };

// ── 索引转换 ──────────────────────────────────────────────────────

/**
 * 花色+数字 → 34 种索引（0-33）
 * @example tileToIndex('m', 1) === 0; tileToIndex('z', 1) === 27
 */
export function tileToIndex(suit: Suit, num: number): TileIndex {
  const offset = SUIT_OFFSET[suit];
  if (suit === 'z') {
    if (num < 1 || num > 7) throw new Error(`字牌数字越界: ${num}`);
    return offset + num - 1;
  }
  if (num < 1 || num > 9) throw new Error(`数牌数字越界: ${num}`);
  return offset + num - 1;
}

/** 34 种索引 → { suit, num } */
export function indexToTile(index: TileIndex): { suit: Suit; num: number } {
  if (index < 0 || index > 33) throw new Error(`牌索引越界: ${index}`);
  if (index < 9) return { suit: 'm', num: index + 1 };
  if (index < 18) return { suit: 'p', num: index - 8 };
  if (index < 27) return { suit: 's', num: index - 17 };
  return { suit: 'z', num: index - 26 };
}

/** 34 种索引 → 牌名（如 "1m", "5z"） */
export function indexToName(index: TileIndex): string {
  return TILE_NAMES[index];
}

/** 牌名（如 "1m"） → 34 种索引 */
export function nameToIndex(name: string): TileIndex {
  const m = /^([1-9])([mpsz])$/.exec(name);
  if (!m) throw new Error(`无法解析牌名: ${name}`);
  return tileToIndex(SUIT_FROM_CHAR[m[2]], Number(m[1]));
}

/** 牌 → 34 种索引 */
export function tileIndex(tile: Tile): TileIndex {
  return tileToIndex(tile.suit, tile.num);
}

// ── 属性判定 ──────────────────────────────────────────────────────

/** 是否字牌 */
export function isHonor(index: TileIndex): boolean {
  return index >= 27;
}

/** 是否数牌 */
export function isNumber(index: TileIndex): boolean {
  return index < 27;
}

/** 是否风牌（东南西北） */
export function isWind(index: TileIndex): boolean {
  return index >= 27 && index <= 30;
}

/** 是否三元牌（白发中） */
export function isDragon(index: TileIndex): boolean {
  return index >= 31 && index <= 33;
}

/** 是否老头牌（1/9 数牌） */
export function isTerminal(index: TileIndex): boolean {
  if (isHonor(index)) return false;
  const num = indexToTile(index).num;
  return num === 1 || num === 9;
}

/** 是否幺九牌（老头牌 + 字牌） */
export function isYaochu(index: TileIndex): boolean {
  return isHonor(index) || isTerminal(index);
}

/** 是否中张（2-8 数牌） */
export function isSimple(index: TileIndex): boolean {
  return !isYaochu(index);
}

/** 同一花色 */
export function sameSuit(a: TileIndex, b: TileIndex): boolean {
  return Math.floor(a / 9) === Math.floor(b / 9) || (isHonor(a) && isHonor(b));
}

// ── 赤宝牌 ────────────────────────────────────────────────────────

/** 赤 5 的索引（5m/5p/5s） */
export const RED_FIVE_INDICES: readonly TileIndex[] = [
  tileToIndex('m', 5),
  tileToIndex('p', 5),
  tileToIndex('s', 5),
];

/** 该索引是否是 5（可作赤宝） */
export function isFive(index: TileIndex): boolean {
  if (isHonor(index)) return false;
  return indexToTile(index).num === 5;
}

// ── 牌山构建 ──────────────────────────────────────────────────────

/**
 * 创建一副完整牌山（136 张牌实例）
 * @param redFives 赤 5 的数量配置，默认每种花色 1 张（共 3 张）
 */
export function createWall(redFives = { m: 1, p: 1, s: 1 } as Record<'m' | 'p' | 's', number>): Tile[] {
  const wall: Tile[] = [];
  let id = 0;
  for (let index = 0; index < NUM_TILE_TYPES; index++) {
    const { suit, num } = indexToTile(index);
    for (let copy = 0; copy < COPIES_PER_TILE; copy++) {
      // 赤宝牌：五万/五筒/五索的前 N 张标记为红
      const isRedCandidate =
        !isHonor(index) && num === 5 && copy < (redFives[suit as 'm' | 'p' | 's'] ?? 0);
      wall.push({ suit, num: num as TileNum, id: id++, red: isRedCandidate });
    }
  }
  return wall;
}

/**
 * Fisher-Yates 洗牌（原地打乱副本）
 * @param random 可注入的随机源（便于测试可复现）
 */
export function shuffle<T>(arr: T[], random: () => number = Math.random): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ── 手牌表示 ──────────────────────────────────────────────────────

/**
 * 手牌计数数组：长度 34，counts[i] = 第 i 种牌的张数
 * 这是引擎内部的主力表示（比 Tile[] 快得多）
 */
export type Counts = readonly number[];

/** 从 Tile[] 构建 counts */
export function tilesToCounts(tiles: readonly Tile[]): number[] {
  const counts = new Array(NUM_TILE_TYPES).fill(0);
  for (const t of tiles) counts[tileIndex(t)]++;
  return counts;
}

/** 从名称字符串构建 counts（如 "123m456p"） */
export function namesToCounts(notation: string): number[] {
  const counts = new Array(NUM_TILE_TYPES).fill(0);
  for (const index of parseNotation(notation)) counts[index]++;
  return counts;
}

/**
 * 解析简写记谱法 → 索引数组
 *
 * 支持：
 *   "123m456p789s11z"  连续数字+花色后缀
 *   "1112345678999m"   同花色多张
 *   "123m 456p"        空格分隔
 *
 * @returns 未排序的索引数组（含重复）
 */
export function parseNotation(notation: string): TileIndex[] {
  const result: TileIndex[] = [];
  // 每组 = 数字串 + 花色字符
  const re = /(\d+)([mpsz])/g;
  let m: RegExpExecArray | null;
  let matched = false;
  while ((m = re.exec(notation)) !== null) {
    matched = true;
    const [, digits, suitChar] = m;
    const suit = SUIT_FROM_CHAR[suitChar];
    for (const ch of digits) {
      result.push(tileToIndex(suit, Number(ch)));
    }
  }
  if (!matched && notation.trim()) {
    throw new Error(`无法解析记谱: ${notation}`);
  }
  return result;
}

/** counts → 名称数组（排序后，如 "1m 1m 2m 3m"） */
export function countsToNames(counts: Counts): string[] {
  const names: string[] = [];
  for (let i = 0; i < NUM_TILE_TYPES; i++) {
    for (let c = 0; c < (counts[i] ?? 0); c++) names.push(TILE_NAMES[i]);
  }
  return names;
}

/** counts → 简写记谱法（如 "1112345678999m"） */
export function countsToNotation(counts: Counts): string {
  const parts: string[] = [];
  for (const suit of ['m', 'p', 's', 'z'] as const) {
    const base = SUIT_OFFSET[suit];
    const len = suit === 'z' ? 7 : 9;
    let digits = '';
    for (let n = 0; n < len; n++) {
      const c = counts[base + n] ?? 0;
      if (c > 0) digits += String(n + 1).repeat(c);
    }
    if (digits) parts.push(digits + suit);
  }
  return parts.join('');
}

/** counts 中的总牌数 */
export function countsTotal(counts: Counts): number {
  let sum = 0;
  for (let i = 0; i < NUM_TILE_TYPES; i++) sum += counts[i] ?? 0;
  return sum;
}

/** 复制 counts */
export function cloneCounts(counts: Counts): number[] {
  return [...counts];
}

/** 两组 counts 是否相等 */
export function countsEquals(a: Counts, b: Counts): boolean {
  for (let i = 0; i < NUM_TILE_TYPES; i++) {
    if ((a[i] ?? 0) !== (b[i] ?? 0)) return false;
  }
  return true;
}

/** 该 counts 中是否存在某种牌超过 4 张（非法手牌） */
export function hasOverFour(counts: Counts): boolean {
  for (let i = 0; i < NUM_TILE_TYPES; i++) if ((counts[i] ?? 0) > 4) return true;
  return false;
}

/** 生成全部 34 种牌的索引列表 */
export function allTileIndices(): number[] {
  return Array.from({ length: NUM_TILE_TYPES }, (_, i) => i);
}
